/**
 * TanStack Query hookovi za oblast platforme (`/platforma`). Ključevi počinju sa `platform` da se ne
 * miješaju sa podacima firme (impersonacija čisti keš firme punom navigacijom).
 */
import type {
  ImpersonateRequest,
  PlanInput,
  PlanPatch,
  PlatformApproveRequest,
  PlatformStatusRequest,
  PlatformSubscriptionInput,
  TenantStatus,
} from '@sm/api';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { unwrap } from './api';
import { platformApi } from './platformApi';

export interface PlatformTenantParams {
  q?: string;
  status?: TenantStatus;
  page?: number;
  pageSize?: number;
}

export interface PlatformAuditParams {
  tenantId?: string;
  action?: string;
  page?: number;
  pageSize?: number;
}

export const pk = {
  me: ['platform', 'me'] as const,
  overview: ['platform', 'overview'] as const,
  tenants: (p: PlatformTenantParams = {}) => ['platform', 'tenants', 'list', p] as const,
  tenant: (id: string) => ['platform', 'tenants', 'detail', id] as const,
  plans: ['platform', 'plans'] as const,
  audit: (p: PlatformAuditParams = {}) => ['platform', 'audit', p] as const,
};

export const platformMeQuery = queryOptions({
  queryKey: pk.me,
  queryFn: () => unwrap(platformApi.GET('/platform/me')),
  staleTime: 60_000,
  retry: false,
});

export function usePlatformMe() {
  return useQuery(platformMeQuery);
}

export function usePlatformOverview(period?: '7' | '30' | '90' | 'lifetime') {
  return useQuery({
    queryKey: [...pk.overview, period ?? 'default'],
    queryFn: () => unwrap(platformApi.GET('/platform/overview', { params: { query: { period } } })),
    refetchInterval: 120_000,
  });
}

export function usePlatformTenants(params: PlatformTenantParams) {
  return useQuery({
    queryKey: pk.tenants(params),
    queryFn: () => unwrap(platformApi.GET('/platform/tenants', { params: { query: params } })),
    placeholderData: keepPreviousData,
  });
}

export function usePlatformTenant(id: string) {
  return useQuery({
    queryKey: pk.tenant(id),
    queryFn: () => unwrap(platformApi.GET('/platform/tenants/{id}', { params: { path: { id } } })),
  });
}

export function usePlatformPlans() {
  return useQuery({
    queryKey: pk.plans,
    queryFn: () => unwrap(platformApi.GET('/platform/plans')),
  });
}

export function usePlatformAudit(params: PlatformAuditParams) {
  return useQuery({
    queryKey: pk.audit(params),
    queryFn: () => unwrap(platformApi.GET('/platform/audit', { params: { query: params } })),
    placeholderData: keepPreviousData,
  });
}

/** Poslije izmjene firme osvježavaju se lista, detalj, pregled i audit. */
function useInvalidateTenants() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });
    void qc.invalidateQueries({ queryKey: pk.overview });
    void qc.invalidateQueries({ queryKey: ['platform', 'audit'] });
    void qc.invalidateQueries({ queryKey: pk.plans });
  };
}

export function useApproveTenant() {
  const done = useInvalidateTenants();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PlatformApproveRequest }) =>
      unwrap(
        platformApi.POST('/platform/tenants/{id}/approve', { params: { path: { id } }, body }),
      ),
    onSuccess: done,
  });
}

export function useRejectTenant() {
  const done = useInvalidateTenants();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      unwrap(
        platformApi.POST('/platform/tenants/{id}/reject', {
          params: { path: { id } },
          body: { reason },
        }),
      ),
    onSuccess: done,
  });
}

export function useSetTenantStatus() {
  const done = useInvalidateTenants();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PlatformStatusRequest }) =>
      unwrap(platformApi.POST('/platform/tenants/{id}/status', { params: { path: { id } }, body })),
    onSuccess: done,
  });
}

export function useUpdateSubscription() {
  const done = useInvalidateTenants();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PlatformSubscriptionInput }) =>
      unwrap(
        platformApi.PUT('/platform/tenants/{id}/subscription', { params: { path: { id } }, body }),
      ),
    onSuccess: done,
  });
}

export function useAddTenantNote() {
  const done = useInvalidateTenants();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      unwrap(
        platformApi.POST('/platform/tenants/{id}/notes', {
          params: { path: { id } },
          body: { body },
        }),
      ),
    onSuccess: done,
  });
}

export function useSavePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; create?: PlanInput; patch?: PlanPatch }) =>
      v.id
        ? unwrap(
            platformApi.PATCH('/platform/plans/{id}', {
              params: { path: { id: v.id } },
              body: v.patch ?? {},
            }),
          )
        : unwrap(platformApi.POST('/platform/plans', { body: v.create as PlanInput })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pk.plans });
      void qc.invalidateQueries({ queryKey: ['platform', 'audit'] });
    },
  });
}

export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(platformApi.DELETE('/platform/plans/{id}', { params: { path: { id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: pk.plans }),
  });
}

export function useRegenerateBackupCodes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      unwrap(platformApi.POST('/platform/me/backup-codes', { body: { code } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: pk.me }),
  });
}

/** Promjena lozinke operatera (trenutna lozinka + TOTP ili rezervni kod). */
export function useChangePlatformPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string; code: string }) =>
      unwrap(platformApi.POST('/platform/me/password', { body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: pk.me }),
  });
}

/** Nova aplikacija za 2FA, 1. korak: trenutni kod → QR nove tajne (stara važi do potvrde). */
export function useStartTotpChange() {
  return useMutation({
    mutationFn: (code: string) => unwrap(platformApi.POST('/platform/me/totp', { body: { code } })),
  });
}

/** Nova aplikacija za 2FA, 2. korak: kod iz nove aplikacije → novi rezervni kodovi. */
export function useConfirmTotpChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      unwrap(platformApi.POST('/platform/me/totp/confirm', { body: { code } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: pk.me }),
  });
}

/**
 * Impersonacija: server postavlja cookie firme (`sm_session`, `sm_csrf`) za korisnika, a panel se
 * učitava iznova punom navigacijom (keš i stanje firme kreću od nule).
 */
export function useImpersonate() {
  return useMutation({
    mutationFn: (body: ImpersonateRequest) =>
      unwrap(platformApi.POST('/platform/impersonate', { body })),
  });
}

/** Završava impersonaciju preko klijenta platforme (`sm_pcsrf`). */
export async function endImpersonation(): Promise<void> {
  await unwrap(platformApi.POST('/platform/impersonation/end'));
}

export async function platformLogout(): Promise<void> {
  await unwrap(platformApi.POST('/platform/auth/logout'));
}
